"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";

type ErrorProps = {
    error: Error & { digest?: string };
    reset: () => void;
};

export default function Error({ error, reset }: ErrorProps) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        console.error(error);
    }, [error]);

    if (!mounted)
        return null;

    return (
        <div className="bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-red-950/20 dark:via-slate-900 dark:to-orange-950/20 flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-20 w-4 h-4 bg-red-300/30 rounded-full animate-float-slow"></div>
                <div className="absolute top-40 right-32 w-6 h-6 bg-orange-300/20 rounded-full animate-float-medium"></div>
                <div className="absolute bottom-32 left-16 w-3 h-3 bg-yellow-300/25 rounded-full animate-float-fast"></div>
                <div className="absolute top-60 left-1/3 w-2 h-2 bg-red-300/30 rounded-full animate-float-slow"></div>
                <div className="absolute bottom-20 right-20 w-5 h-5 bg-orange-300/20 rounded-full animate-float-medium"></div>

                <div className="absolute top-32 right-16 w-8 h-8 border-2 border-red-200/40 dark:border-red-400/20 rotate-45 animate-pulse"></div>
                <div className="absolute bottom-40 left-32 w-6 h-6 border-2 border-orange-200/40 dark:border-orange-400/20 animate-spin-slow"></div>
            </div>

            <div className="text-center max-w-2xl mx-auto relative z-10">
                <div className="animate-fade-in-up">
                    <h1 className="text-7xl md:text-8xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">
                        Whoops!
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 mb-8 font-medium">
                        Something went wrong
                    </p>
                </div>

                <div className="animate-fade-in-up animation-delay-200">
                    <div className="relative inline-block">
                        <svg
                          width="240"
                          height="180"
                          viewBox="0 0 240 180"
                          className="mx-auto"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                              x="80"
                              y="60"
                              width="80"
                              height="60"
                              rx="8"
                              stroke="currentColor"
                              strokeWidth="3"
                              fill="none"
                              className="text-slate-700 dark:text-slate-300"
                            />

                            <rect
                              x="90"
                              y="70"
                              width="60"
                              height="40"
                              rx="4"
                              fill="currentColor"
                              className="text-slate-200 dark:text-slate-700"
                            />

                            <path
                              d="M105 85 L135 105 M135 85 L105 105"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              className="text-red-500 animate-pulse"
                            />

                            <path
                              d="M100 120 L100 140 M140 120 L140 140"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              className="text-slate-700 dark:text-slate-300"
                            />

                            <path
                              d="M80 80 L60 90 M160 80 L180 90"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              className="text-slate-700 dark:text-slate-300"
                            />

                            <path d="M50 70 L55 65 L50 60 L45 65 Z" fill="currentColor" className="text-yellow-400 animate-pulse" />
                            <path
                              d="M190 75 L195 70 L190 65 L185 70 Z"
                              fill="currentColor"
                              className="text-orange-400 animate-pulse animation-delay-300"
                            />
                            <path
                              d="M70 45 L75 40 L70 35 L65 40 Z"
                              fill="currentColor"
                              className="text-red-400 animate-pulse animation-delay-600"
                            />
                        </svg>
                    </div>
                </div>

                <div className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-slate-200/50 dark:border-slate-700/50 animate-fade-in-up animation-delay-300">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Error Details</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 font-mono bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                        {error.message || "An unexpected error occurred"}
                    </p>
                    {error.digest && (
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                            Error ID:
                            {error.digest}
                        </p>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
                    <Button
                      onClick={reset}
                      size="lg"
                      className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                        Try Again
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      className="border-2 border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 bg-transparent"
                    >
                        <Link prefetch href="/">Go Home</Link>
                    </Button>
                </div>

                <p className="text-sm text-slate-500 dark:text-slate-400 mt-8 animate-fade-in-up animation-delay-600">
                    If this problem persists, please contact our support team.
                </p>
            </div>
        </div>
    );
}
